import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from './utils';

export type BadgeIntent =
  | 'neutral'
  | 'info'
  | 'success'
  | 'warning'
  | 'error'
  | 'accent'
  | 'primary';

/**
 * Matches `TripItemStatus` values so trip-item badges can be rendered by
 * passing the status directly.
 */
export type BadgeStatus = 'proposed' | 'ready_to_book' | 'booked' | 'n_a';

export type BadgeSize = 'sm' | 'md';

export interface BadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  intent?: BadgeIntent;
  /** Convenience prop — if set, overrides `intent` with status-specific styling. */
  status?: BadgeStatus;
  size?: BadgeSize;
  children: ReactNode;
}

const intentStyles: Record<BadgeIntent, string> = {
  neutral: 'bg-neutral-100 text-neutral-700',
  info: 'bg-info-bg text-info-fg',
  success: 'bg-success-bg text-success-fg',
  warning: 'bg-warning-bg text-warning-fg',
  error: 'bg-error-bg text-error-fg',
  accent: 'bg-accent/20 text-primary',
  primary: 'bg-primary text-white',
};

const statusStyles: Record<BadgeStatus, string> = {
  proposed: 'bg-info-bg text-info-fg',
  ready_to_book: 'bg-warning-bg text-warning-fg',
  booked: 'bg-success-bg text-success-fg',
  n_a: 'bg-neutral-100 text-neutral-500',
};

const sizeStyles: Record<BadgeSize, string> = {
  sm: 'text-xs px-1.5 py-0.5',
  md: 'text-sm px-2 py-0.5',
};

const baseStyles = 'inline-flex items-center gap-1 font-medium rounded';

export function Badge({
  intent = 'neutral',
  status,
  size = 'sm',
  className = '',
  children,
  ...rest
}: BadgeProps) {
  const variantCls = status ? statusStyles[status] : intentStyles[intent];
  const cls = cn(baseStyles, variantCls, sizeStyles[size], className);
  return (
    <span className={cls} {...rest}>
      {children}
    </span>
  );
}
